import { useEffect, useSyncExternalStore } from "react";
import { store } from "./in-view";

/** The app's own name, which every page's title ends with (BUILD_SPEC §4.5). */
export const APP_TITLE = "Steer by Optymyse";

/** "<page> · Steer by Optymyse", or the app's name alone for a page with no name of its own. */
export function titleOf(page?: string | null): string {
  return page ? `${page} · ${APP_TITLE}` : APP_TITLE;
}

// A page's own name, and the frame's for where it is: its section in the rail. The page's comes first.
const pageName = store<string>();
const placeName = store<string>();

/** Keeps a name in `named` while it is given, and takes it back as it goes. */
function useNamed(named: ReturnType<typeof store<string>>, name: string | null | undefined): void {
  useEffect(() => {
    if (!name) return;
    named.set(name);
    return () => {
      if (named.get() === name) named.set(null);
    };
  }, [named, name]);
}

/**
 * Names the page in the browser's title while it is shown — an issue by its key and title, a project's view by the
 * project's name — over the name of its section in the rail, which the frame gives it.
 */
export function usePageTitle(name: string | null | undefined): void {
  useNamed(pageName, name);
}

/** The same for a page that is one branch of another, such as a door shown in place of the console. */
export function PageTitle({ name }: { name: string }) {
  usePageTitle(name);
  return null;
}

/** The frame's name for the page: its section in the rail, for a page that gives none of its own. */
export function usePlaceTitle(name: string | null | undefined): void {
  useNamed(placeName, name);
}

/** Keeps the browser's title, for the app's root: the page's own name, else its place's, then the app's alone. */
export function useDocumentTitle(): void {
  const page = useSyncExternalStore(pageName.subscribe, pageName.get, () => null);
  const place = useSyncExternalStore(placeName.subscribe, placeName.get, () => null);
  useEffect(() => {
    document.title = titleOf(page ?? place);
  }, [page, place]);
}
